/**
 * Creates a new object with specified properties omitted
 * Performance-optimized version of lodash omit
 */
export function omit<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: any[] // K[] | readonly K[]
): Omit<T, K> {
  // Handle edge cases
  if (!obj || typeof obj !== "object") {
    return {} as Omit<T, K>;
  }

  if (!keys || keys.length === 0) {
    return { ...obj } as Omit<T, K>;
  }

  // Use Set for O(1) lookup performance when checking if key should be omitted
  const keysToOmit = new Set(keys);

  // Create result object
  const result = {} as Omit<T, K>;

  // Iterate through object keys only once
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && !keysToOmit.has(key as keyof T as K)) {
      (result as any)[key] = obj[key];
    }
  }

  return result;
}

/**
 * Creates a curried version of omit for better reusability
 * Usage: const omitId = omitBy(['id']); const cleanUser = omitId(user);
 */
export function omitBy<K extends string>(keys: K[] | readonly K[]) {
  return <T extends Record<string, any>>(obj: T): Omit<T, K> => {
    return omit(obj, keys as K[]);
  };
}

/**
 * Type-safe omit that ensures keys exist on the object
 */
export function omitSafe<T extends Record<string, any>>(
  obj: T,
  ...keys: Array<keyof T>
): Omit<T, keyof T> {
  return omit(obj, keys);
}
